/**
 * /plugins/<id> — one place for a plugin, from its first draft to its runs.
 *
 * A strip of steps under the head, each with its state beneath its name, and
 * the step's work below it (the frame's page column is the only left column). Details,
 * Tools and Preview share one draft of the manifest, loaded whole from
 * `GET /plugins/<id>`; a save sends only what changed, is checked first with
 * `changes-preview`, and asks before it makes people turn it on again.
 */
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import api from '../../services/api';
import { Button, Modal, useToast } from '../../components/common';
import DetailsStep from './DetailsStep';
import ToolsStep from './ToolsStep';
import PreviewStep, { Changes, call, missingRoute } from './PreviewStep';
import { PANELS } from './panelSlots';
import {
  bodyFromDraft, draftFromPlugin, isEmptyBody, kindLabel, stepStates, toErrors,
} from './manifest';
import './PluginWorkspace.css';

const STEPS = [
  { id: 'details', name: 'Details' },
  { id: 'tools', name: 'Tools' },
  { id: 'preview', name: 'Preview' },
  { id: 'test', name: 'Test' },
  { id: 'verification', name: 'Verification' },
  { id: 'publish', name: 'Publish' },
  { id: 'runs', name: 'Runs' },
  { id: 'settings', name: 'Settings' },
];
const EDITING = ['details', 'tools', 'preview'];

export default function PluginWorkspace() {
  const { pluginId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const step = STEPS.some((s) => s.id === params.get('step')) ? params.get('step') : 'details';

  const [plugin, setPlugin] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null);
  const [revision, setRevision] = useState(0);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // a changes-preview awaiting a yes

  const base = useMemo(() => (plugin ? draftFromPlugin(plugin) : null), [plugin]);

  const reload = useCallback(async ({ keepDraft = false } = {}) => {
    try {
      const r = await api.getPlugin(pluginId);
      const p = r?.plugin || r;
      setPlugin(p);
      setLoadError('');
      if (!keepDraft) {
        setDraft(draftFromPlugin(p));
        setRevision((n) => n + 1);
      }
      return p;
    } catch (e) {
      setLoadError(e.status === 404 ? 'There is no such plugin, or it isn\'t yours.' : (e.message || 'Could not load it.'));
      return null;
    }
  }, [pluginId]);

  // A panel's reload re-reads the plugin but keeps unsaved edits.
  const panelReload = useCallback(() => reload({ keepDraft: true }), [reload]);

  useEffect(() => { reload(); }, [reload]);

  const body = useMemo(() => (draft && base ? bodyFromDraft(draft, base) : {}), [draft, base]);
  const dirty = !isEmptyBody(body);
  const states = useMemo(() => (plugin && draft ? stepStates(plugin, draft, { dirty }) : {}), [plugin, draft, dirty]);

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // The steps are a strip that scrolls sideways when narrow; keep the
  // standing one in view.
  useEffect(() => {
    const on = document.querySelector('.pw-steps [aria-current="step"]');
    on?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [step, plugin]);

  const go = (id) => setParams(id === 'details' ? {} : { step: id }, { replace: true });

  const put = async () => {
    setConfirm(null);
    setSaving(true);
    try {
      await api.updatePlugin(pluginId, body);
      setErrors([]);
      await reload();
      toast.success('Saved.');
    } catch (e) {
      setErrors(toErrors(e.body?.errors || e));
    }
    setSaving(false);
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setErrors([]);
    let preview = null;
    try {
      preview = await call('previewChanges', pluginId, body);
    } catch (e) {
      if (!missingRoute(e)) {
        setErrors(toErrors(e.body?.errors || e));
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    if (preview && preview.valid === false) {
      setErrors(toErrors(preview.errors || []));
      return;
    }
    if (preview?.reapproval) {
      setConfirm(preview);
      return;
    }
    await put();
  };

  const discard = () => {
    setDraft(draftFromPlugin(plugin));
    setErrors([]);
    setRevision((n) => n + 1);
  };

  if (loadError) {
    return (
      <div className="pw pw--page standalone-page ofself-page-fade">
        <p className="pw__err">{loadError}</p>
        <Button variant="secondary" onClick={() => navigate('/plugins')}>All plugins</Button>
      </div>
    );
  }
  if (!plugin || !draft) return <div className="pw pw--page"><p className="pw__muted">Loading…</p></div>;

  const Panel = PANELS[step];
  const unplaced = errors.filter((e) => !e.field);
  const toolName = (id) => (draft.tools || []).find((t) => t.tool_id === id)?.name || id;
  const lapsing = confirm?.approvals_lapsing || 0;

  return (
    <div className="pw-section">
        <div className="pw-section__pane">
          <div className="pw" key={revision}>
            <header className="pw__head">
              <button type="button" className="pw__back" onClick={() => navigate('/plugins')}>All plugins</button>
              <h1 className="pw__title">{plugin.name}</h1>
              <p className="pw__facts">
                <span>{kindLabel(plugin.execution_type)}</span>
                <span>v{plugin.spec_version || 1}</span>
                <span>{(plugin.deployment_mode || 'incubator') === 'public' ? 'Public' : 'Incubator'}</span>
                <span>{plugin.is_verified ? 'Verified by ofSelf' : 'Not verified'}</span>
              </p>
            </header>

            {/* THE STEPS, as a strip under the head. They were a second left
                column inside the frame's own page column — three columns of
                navigation before the page. Each step says where it stands. */}
            <nav className="pw-steps" aria-label="Steps">
              {STEPS.map((s) => {
                const st = states[s.id];
                return (
                  <button key={s.id} type="button" className="pw-steps__step"
                    aria-current={step === s.id ? 'step' : undefined} onClick={() => go(s.id)}>
                    <span className="pw-steps__name">{s.name}</span>
                    <span className="pw-steps__state" data-done={st?.done || undefined}>{st?.status}</span>
                  </button>
                );
              })}
            </nav>

            {step === 'details' ? <DetailsStep draft={draft} setDraft={setDraft} disabled={saving} errors={errors} /> : null}
            {step === 'tools' ? <ToolsStep draft={draft} setDraft={setDraft} base={base} disabled={saving} errors={errors} /> : null}
            {step === 'preview' ? <PreviewStep plugin={plugin} draft={draft} body={body} /> : null}
            {Panel ? (
              <Suspense fallback={<p className="pw__muted">Loading…</p>}>
                <Panel plugin={plugin} reload={panelReload} />
              </Suspense>
            ) : null}
          </div>

          {(dirty || unplaced.length) && EDITING.includes(step) ? (
            <div className="pw__savebar" role="region" aria-label="Unsaved changes">
              <div className="pw__savebar-text">
                {unplaced.map((e, i) => <span key={i} className="pw__err">{e.message}</span>)}
                {errors.length > unplaced.length ? (
                  <span className="pw__err">{errors.length - unplaced.length} to fix, shown in place.</span>
                ) : null}
                {dirty && !errors.length ? <span>Unsaved changes</span> : null}
              </div>
              <Button variant="ghost" onClick={discard} disabled={saving || !dirty}>Discard</Button>
              <Button onClick={save} loading={saving} disabled={!dirty}>Save</Button>
            </div>
          ) : null}
        </div>

      <Modal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={`${lapsing} ${lapsing === 1 ? 'person or group turns' : 'people and groups turn'} it on again`}
        size="md"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>Keep editing</Button>
            <Button onClick={put}>Save</Button>
          </>
        )}
      >
        <div className="pw">
          <p className="pw__line">They review what changed first. Until then its tools don’t run for them: not automatically, and not for their apps and agents.</p>
          <Changes changes={confirm?.changes} toolName={toolName} />
        </div>
      </Modal>
    </div>
  );
}
